import React, { useEffect, useMemo, useState } from 'react';
import { PiX } from 'react-icons/pi';
import Tag from 'src/modules/shared/components/atoms/tag';
import SearchInput from 'src/modules/shared/components/molecules/inputs/search-input';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { PageFiltersBar } from 'src/modules/shared/components/molecules/page-filters-bar';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  type TeamIdFilter,
  type UserFiltersProps,
  userProfileFilterSchema,
  userStatusFilterSchema,
} from 'src/modules/users/types/user-filters.types';

export const UserFilters: React.FC<UserFiltersProps> = ({
  filters,
  teams,
  onFiltersChange,
  onClearFilters,
}) => {
  const { t } = useI18n();
  const handleStatusChange = (status: string) => {
    onFiltersChange({
      ...filters,
      status: userStatusFilterSchema.parse(status),
    });
  };

  const handleProfileChange = (profile: string) => {
    onFiltersChange({
      ...filters,
      profile: userProfileFilterSchema.parse(profile),
    });
  };

  const handleTeamChange = (teamId: TeamIdFilter) => {
    onFiltersChange({
      ...filters,
      teamId,
    });
  };

  const [searchText, setSearchText] = useState<string>(filters.search ?? '');

  useEffect(() => {
    setSearchText(filters.search ?? '');
  }, [filters.search]);

  const handleSearchCommit = () => {
    onFiltersChange({
      ...filters,
      search: searchText,
    });
  };

  const statusOptions = [
    { id: 'all', content: t('users.filters.status.all') },
    { id: 'active', content: t('users.filters.status.active') },
    { id: 'inactive', content: t('users.filters.status.inactive') },
  ];

  const profileOptions = [
    { id: 'all', content: t('users.filters.profile.all') },
    { id: 'admin', content: t('users.filters.profile.admin') },
    { id: 'agent', content: t('users.filters.profile.agent') },
  ];

  const teamOptions = [
    { id: 'all', content: t('users.filters.team.all') },
    ...teams.map((team) => ({
      id: team.id.toString(),
      content:
        team.name ?? t('users.filters.team.fallback', { id: `${team.id}` }),
    })),
  ];

  const activeFiltersCount =
    (filters.status && filters.status !== 'all' ? 1 : 0) +
    (filters.profile && filters.profile !== 'all' ? 1 : 0) +
    (filters.teamId && filters.teamId !== 'all' ? 1 : 0) +
    (filters.search ? 1 : 0);

  const filteredTeam = teams.find((team) => team.id === filters.teamId);

  const teamFilterContent = useMemo(() => {
    if (!filters.teamId || filters.teamId === 'all') {
      return t('users.filters.team.currentAll');
    }

    return t('users.filters.team.current', {
      name: filteredTeam?.name ?? '',
    });
  }, [filters.teamId, filteredTeam, t]);

  const statusFilterContent = useMemo(() => {
    if (!filters.status || filters.status === 'all') {
      return t('users.filters.status.currentAll');
    }

    return t('users.filters.status.current', {
      value:
        filters.status === 'active'
          ? t('users.filters.status.active')
          : t('users.filters.status.inactive'),
    });
  }, [filters.status, t]);

  const profileFilterContent = useMemo(() => {
    if (!filters.profile || filters.profile === 'all') {
      return t('users.filters.profile.currentAll');
    }

    return t('users.filters.profile.current', {
      value:
        filters.profile === 'admin'
          ? t('users.filters.profile.admin')
          : t('users.filters.profile.agent'),
    });
  }, [filters.profile, t]);

  const searchFilterContent = useMemo(() => {
    if (!filters.search) {
      return t('users.filters.search.currentAll');
    }

    return t('users.filters.search.current', { value: filters.search });
  }, [filters.search, t]);

  return (
    <PageFiltersBar>
      <div className="flex w-full min-w-0 flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] max-w-[300px]">
            <SearchInput
              value={searchText}
              onChange={setSearchText}
              onSearch={handleSearchCommit}
              placeholder={t('users.filters.search.placeholder')}
              size="md"
            />
          </div>

          <MenuDropdown
            label={
              <Text color="neutral" size="small">
                {statusFilterContent}
              </Text>
            }
            variant="primary"
            appearance="outline"
            size="md"
            items={statusOptions.map((option) => ({
              ...option,
              onClick: () => handleStatusChange(option.id.toString()),
            }))}
          />

          <MenuDropdown
            label={
              <Text color="neutral" size="small">
                {profileFilterContent}
              </Text>
            }
            variant="primary"
            appearance="outline"
            size="md"
            items={profileOptions.map((option) => ({
              ...option,
              onClick: () => handleProfileChange(option.id.toString()),
            }))}
          />

          <MenuDropdown
            label={
              <Text color="neutral" size="small">
                {teamFilterContent}
              </Text>
            }
            variant="primary"
            appearance="outline"
            size="md"
            items={teamOptions.map((option) => ({
              ...option,
              onClick: () => {
                if (option.id === 'all') {
                  handleTeamChange('all');
                } else {
                  const teamId = parseInt(option.id.toString());
                  handleTeamChange(teamId);
                }
              },
            }))}
          />

          {activeFiltersCount > 0 && (
            <Button
              variant="primary"
              appearance="soft"
              size="sm"
              onClick={() => {
                setSearchText('');
                onClearFilters();
              }}
              className="ml-2"
            >
              <PiX className="w-4 h-4" />
              <Text size="small">
                {t('users.filters.clearWithCount', {
                  count: `${activeFiltersCount}`,
                })}
              </Text>
            </Button>
          )}
        </div>

        {activeFiltersCount > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <Text size="small" color="neutral">
              {t('users.filters.active')}
            </Text>

            {filters.status && filters.status !== 'all' && (
              <Tag
                content={
                  <div className="flex items-center gap-2">
                    <Text color="neutral" size="tiny">
                      {statusFilterContent}
                    </Text>
                    <PiX className="w-3 h-3" />
                  </div>
                }
                variant="primary"
                size="md"
                onClick={() => handleStatusChange('all')}
              />
            )}

            {filters.profile && filters.profile !== 'all' && (
              <Tag
                content={
                  <div className="flex items-center gap-2">
                    <Text color="neutral" size="tiny">
                      {profileFilterContent}
                    </Text>
                    <PiX className="w-3 h-3" />
                  </div>
                }
                variant="primary"
                size="md"
                onClick={() => handleProfileChange('all')}
              />
            )}

            {filters.teamId && filters.teamId !== 'all' && (
              <Tag
                content={
                  <div className="flex items-center gap-2">
                    <Text color="neutral" size="tiny">
                      {teamFilterContent}
                    </Text>
                    <PiX className="w-3 h-3" />
                  </div>
                }
                variant="primary"
                size="md"
                onClick={() => handleTeamChange('all')}
              />
            )}

            {filters.search && (
              <Tag
                content={
                  <div className="flex items-center gap-2">
                    <Text color="neutral" size="tiny">
                      {searchFilterContent}
                    </Text>
                    <PiX className="w-3 h-3" />
                  </div>
                }
                variant="primary"
                size="md"
                onClick={() => {
                  setSearchText('');
                  onFiltersChange({ ...filters, search: '' });
                }}
              />
            )}
          </div>
        )}
      </div>
    </PageFiltersBar>
  );
};
